import {
    RiAddLine, RiCalendarEventLine, RiDeleteBinLine, RiEdit2Line, RiMoreFill,
} from "@remixicon/react";
import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { TicketTask, TicketTaskInput, TicketTaskState } from "../../../../../__generated__/api";
import { TicketItem } from "../../../../../contexts/ticket-state";
import { tryParseDate } from "../../../../../date";
import Confirm from "../../../../confirm/Confirm";
import useDeleteConfirm from "../../../../confirm/Delete";
import DateCalendarFilterPicker from "../../../../pickers/date-range/DateCalendarFilterPicker";
import Users from "../../../../pickers/users/Users";
import Badge from "../../../../theme/badge/Badge";
import Button from "../../../../theme/button/Button";
import Dropdown from "../../../../theme/dropdown/Dropdown";
import FormGroup from "../../../../theme/form-group/FormGroup";
import DateDisplay from "../../../labels/date/Date";
import TaskStatus from "../../../labels/task-status/TaskStatus";
import UserDisplay from "../../../labels/user/User";
import Styles from "../TicketDetails.module.scss";
import {
    DetailSection,
    TicketProperties, sectionRegistry,
} from "./base";
import { LoggingHandler } from "../../../../../contexts/logging/LoggingContext";

function TaskCreate({
    ticket, shouldShow, onHide, task, metricPath,
}: {
    ticket: TicketItem;
    task?: TicketTask;
    shouldShow: boolean;
    onHide: () => void;
    metricPath: string[];
}) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [dueDate, setDueDate] = useState<Date>();
    const [assigneeId, setAssigneeId] = useState<string>();

    useEffect(() => {
        setTitle(task?.name ?? "");
        setDescription(task?.description ?? "");
        setDueDate(task?.dueDate);
        setAssigneeId(task?.assigneeId);
    }, [task, shouldShow]);

    const save = () => {
        ticket.tasks.save({
            id: task?.id,
            name: title,
            description,
            dueDate,
            assigneeId,
            status: task?.status ?? TicketTaskState.Open,
        });
        onHide();
    };

    return (
        <Confirm
            title="Create Task"
            shouldShow={shouldShow}
            confirmText="Create"
            onConfirm={() => save()}
            confirmMetricPath={[...metricPath, "saved"]}
            onHide={() => onHide()}
        >

            <FormGroup
                type="text"
                label="Title"
                isRequired
                size="small"
                groupClassName={Styles["task-create-field"]}
                onChange={evt => setTitle(evt.target.value)}
                value={title}
            />
            <FormGroup
                type="text"
                display="textarea"
                label="Description"
                groupClassName={Styles["task-create-field"]}
                onChange={evt => setDescription(evt.target.value)}
                value={description}
            />
            <FormGroup type="text" display="textarea" label="Assignee" groupClassName={Styles["task-create-field"]}>
                <Users
                    value={assigneeId}
                    onSelected={id => {
                        setAssigneeId(id);
                    }}
                />
            </FormGroup>
            <FormGroup
                type="datetime-local"
                label="Due Date"
                groupClassName={Styles["task-create-field"]}
            >
                <DateCalendarFilterPicker
                    value={dueDate}
                    onChange={date => {
                        setDueDate(date);
                    }}
                    allowTime
                />
            </FormGroup>

        </Confirm>
    );
}
TaskCreate.defaultProps = {
    task: undefined,
};

function TaskRow({
    ticket, task, onEdit, onDelete, isEditable, metricPathPrefix,
}: {
    ticket: TicketItem;
    task: TicketTask;
    onEdit: () => void;
    onDelete: () => void;
    isEditable: boolean;
    metricPathPrefix: string[];
}) {
    const save = (updates: Partial<TicketTaskInput>) => {
        ticket.tasks.save({
            id: task.id,
            name: updates.name ?? task.name,
            description: updates.description ?? task.description,
            dueDate: updates.dueDate ?? task.dueDate,
            assigneeId: updates.assigneeId ?? task.assigneeId,
            status: updates.status ?? task.status,
        });
    };

    return (
        <div className={classNames(Styles["task-item"], isEditable ? Styles["task-item-editable"] : "")}>
            <div className={Styles["task-item-header"]}>
                <TaskStatus
                    task={task}
                    status={task.status}
                    onChange={status => {
                        LoggingHandler.instance.metric([
                            ...metricPathPrefix, "edit", "status", status.toLowerCase().replace(/_/g, "-"),
                        ]);
                        ticket.tasks.saveStatus(task.id, status);
                    }}
                />
                {isEditable && (
                    <Dropdown>
                        <Dropdown.Toggle variant="ghost-gray" size="xsmall" prefixIcon={<RiMoreFill />} />
                        <Dropdown.Menu>
                            <Dropdown.Item
                                metricPath={[...metricPathPrefix, "edit"]}
                                onClick={onEdit}
                                prefixIcon={<RiEdit2Line />}
                            >
                                Edit
                            </Dropdown.Item>
                            <Dropdown.Item
                                metricPath={[...metricPathPrefix, "delete"]}
                                onClick={onDelete}
                                prefixIcon={<RiDeleteBinLine />}
                            >
                                Delete
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                )}

            </div>
            <div className={Styles["task-title"]}>
                {task.name}
            </div>
            <div className={Styles["task-description"]}>
                {task.description}
            </div>
            <div className={Styles["task-meta"]}>
                <div className={Styles["task-meta-date"]}>
                    <RiCalendarEventLine />
                    {" "}
                    <DateDisplay
                        value={tryParseDate(task.dueDate)}
                        onChange={!isEditable ? undefined : dueDate => {
                            LoggingHandler.instance.metric([...metricPathPrefix, "edit", "due-date"]);
                            save({ dueDate });
                        }}
                        defaultText={(
                            <span className={Styles["task-default-text"]}>No Due Date</span>
                        )}
                    />
                </div>
                <div>
                    <UserDisplay
                        allowOverlay={false}
                        user={task.assignee}
                        onChange={!isEditable ? undefined : assigneeId => {
                            LoggingHandler.instance.metric([...metricPathPrefix, "edit", "assignee"]);
                            save({ assigneeId });
                        }}
                        defaultText={(
                            <span className={Styles["task-default-text"]}>Not Assigned</span>
                        )}
                    />
                </div>

            </div>

        </div>
    );
}

export default function TasksSection({
    ticket, isMinimized, onToggleExpand, metricPathPrefix,
}: TicketProperties) {
    ticket.useChatDetails();
    // const [status, setStatus] = useState<TicketTaskState>(() => task.status);
    const metricPath = useMemo(() => [...metricPathPrefix, "tasks"], metricPathPrefix);

    const [isShowCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState<TicketTask>();
    const deleteConfirm = useDeleteConfirm<TicketTask>(item => {
        ticket.tasks.delete(item?.id);
        LoggingHandler.instance.metric([...metricPath, "delete", "confirm"]);
    }, item => item.name);

    const { finished, inProgress } = ticket.tasks;
    const isAllDone = finished.isNotEmpty && inProgress.isEmpty;
    return (
        <DetailSection
            title={(
                <div className={Styles["task-header"]}>
                    <div>
                        Tasks
                        <Badge isRounded variant={isAllDone ? "success" : "main"}>{ticket.tasks?.length}</Badge>
                    </div>
                    <Button
                        variant="none"
                        size="xsmall"
                        prefixIcon={<RiAddLine />}
                        className={Styles["task-create"]}
                        metricPath={[...metricPath, "create"]}
                        onClick={evt => {
                            evt.preventDefault();
                            evt.stopPropagation();
                            setShowCreate(true);
                        }}
                    >
                        Create Task
                    </Button>

                </div>
            )}
            isMinimized={isMinimized}
            className={Styles["task-section"]}
            onToggleExpand={onToggleExpand}

        >
            <TaskCreate
                task={editing}
                shouldShow={isShowCreate || editing != null}
                onHide={() => {
                    setShowCreate(false);
                    setEditing(undefined);
                }}
                ticket={ticket}
                metricPath={[...metricPath, editing != null ? "edit" : "create"]}
            />

            {deleteConfirm.element}

            <div className={Styles["task-container"]}>

                {inProgress?.map(task => (
                    <TaskRow
                        key={task.id}
                        task={task}
                        ticket={ticket}
                        isEditable
                        onEdit={() => setEditing(task)}
                        onDelete={() => deleteConfirm.setDeleting(task)}
                        metricPathPrefix={metricPath}
                    />
                ))}

                {finished?.isNotEmpty && (
                    <>
                        {!isAllDone && (
                            <div>
                                Completed Tasks
                                {" "}
                                <Badge isRounded variant="main">{finished.length}</Badge>
                            </div>
                        )}

                        {finished?.map(task => (
                            <TaskRow
                                key={task.id}
                                task={task}
                                ticket={ticket}
                                isEditable={false}
                                onEdit={() => setEditing(task)}
                                onDelete={() => deleteConfirm.setDeleting(task)}
                                metricPathPrefix={metricPath}
                            />
                        ))}
                    </>
                )}

            </div>

        </DetailSection>

    );
}

sectionRegistry.register({
    sectionId: "urn:fq:portal-chat-section:tasks",
    label: "Tasks",
    component: TasksSection,
});
